import { motion, useReducedMotion, type Variants } from 'motion/react';
import Heading from '@/components/Heading/Heading';
import { getSlideDuration } from '@/components/HeroCarousel/HeroCarousel';
import type { HeroFullOverlayContent } from '@/components/TopLevelComponents/HeroFullOverlay/HeroFullOverlay';
import useStyles from './ProgressBox.styles';

interface ProgressBarProps {
  active: boolean;
  slide: HeroFullOverlayContent | undefined;
  onClick: () => void;
}

export default function ProgressBox({
  active,
  slide,
  onClick,
}: ProgressBarProps) {
  const { classes, cx } = useStyles();
  const prefersReducedMotion = useReducedMotion();

  const heading = slide?.fields.carouselSlideHeading || slide?.fields.heading;
  const subhead =
    slide?.fields.carouselSlideSubhead || slide?.fields.subheading;

  const durationInSeconds = getSlideDuration(slide) / 1000;
  const progressBarVariants: Variants = {
    active: {
      width: '100%',
      transition: {
        duration: prefersReducedMotion ? 0 : durationInSeconds,
        ease: 'linear',
      },
    },
    inactive: {
      width: '0%',
      transition: {
        duration: 0,
      },
    },
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Automated migration to Biome
    // biome-ignore lint/a11y/useKeyWithClickEvents: Automated migration to Biome
    <div
      className={classes.progressBox}
      onClick={onClick}
      data-testid='progress-box'
      data-is-active={active}
    >
      <div className={classes.progressBarTrack}>
        <motion.div
          className={classes.progressBar}
          variants={progressBarVariants}
          animate={active ? 'active' : 'inactive'}
          initial='inactive'
        />
      </div>
      <div
        className={cx(classes.progressDescription, {
          [classes.progressDescriptionActive]: active,
        })}
      >
        {heading && (
          <Heading level='h2' classNameOverride={classes.heading}>
            {heading}
          </Heading>
        )}
        {subhead && <p className={classes.subheading}>{subhead}</p>}
      </div>
    </div>
  );
}
